import React, { createContext, useEffect, useState, startTransition } from 'react'

export interface ConsoleMessage {
    type: 'log' | 'error' | 'warn'
    message: string
}

export type ContextType = {
    consoleMessages: ConsoleMessage[]
}

// Create the context with default values
export const ContextConsole = createContext<ContextType>({
    consoleMessages: [] as ConsoleMessage[],
})

const ContextConsoleProvider = ({ children }) => {
    const [consoleMessages, setConsoleMessages] = useState<ConsoleMessage[]>([])

    useEffect(() => {
        const originalConsoleLog = console.log
        const originalConsoleError = console.error
        const originalConsoleWarn = console.warn

        console.log = (...args: any[]) => {
            handleConsoleOutput('log', args)
            originalConsoleLog(...args)
        }

        console.error = (...args: any[]) => {
            // Check if response data is available as the last argument
            const errorObject = args.find((arg) => arg && arg.response && arg.response.data)

            if (errorObject) {
                // Log response data if available
                console.log('Response Data:', errorObject.response.data)
                handleConsoleOutput('error', args.concat(`Response Data: ${JSON.stringify(errorObject.response.data)}`))
            } else {
                handleConsoleOutput('error', args)
            }
            originalConsoleError(...args)
        }

        console.warn = (...args: any[]) => {
            handleConsoleOutput('warn', args)
            originalConsoleWarn(...args)
        }

        function handleConsoleOutput(type: 'log' | 'error' | 'warn', args: any[]) {
            const message = args.map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : arg)).join(' ')
            // Step 1: Defer state update to avoid updating during render
            startTransition(() => {
                setConsoleMessages((currentMessages) => [...currentMessages, { type, message }])
            })
        }

        return () => {
            console.log = originalConsoleLog
            console.error = originalConsoleError
            console.warn = originalConsoleWarn
        }
    }, [])

    return (
        <ContextConsole.Provider
            value={{
                consoleMessages,
            }}
        >
            {children}
        </ContextConsole.Provider>
    )
}

export default ContextConsoleProvider
